import { Check, Lock } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

interface AttributePillProps {
  label: string;
  selected: boolean;
  /** Locked by an unmet dependency — inert, and never included in the payload. */
  locked: boolean;
  /** Always enriched: rendered selected and inert with a lock, and can't be
   *  turned off. Distinct from `locked`, which is a *deselected* unavailable state. */
  mandatory?: boolean;
  onToggle: () => void;
  /** Id of the visible element explaining why this pill is locked or mandatory. */
  describedBy?: string;
  /** Hover/focus hint — e.g. that a mandatory pill can't be turned off. Rendered
   *  as a tooltip *in addition to* the visible reason line, never as the only cue.
   *  The pill uses `aria-disabled` (not `disabled`), so it stays hoverable and the
   *  tooltip fires even in the inert states. */
  tooltip?: string;
  /** `data-testid` for the pill button. */
  testId?: string;
}

/**
 * One selectable attribute, rendered as a capsule with an inline checkbox. Four
 * visual states: off, on, locked (unavailable, off), and mandatory (always on).
 *
 * Both inert states stay in the DOM and in the tab order as `aria-disabled`
 * rather than using the `disabled` attribute, so a keyboard or screen-reader user
 * can reach them and hear why — a `disabled` control is skipped entirely and the
 * explanation never reaches them.
 */
export function AttributePill({
  label,
  selected,
  locked,
  mandatory = false,
  onToggle,
  describedBy,
  tooltip,
  testId,
}: AttributePillProps) {
  // Mandatory pills read as selected; only a plain, unlocked pill is interactive.
  const isChecked = selected || mandatory;
  const interactive = !locked && !mandatory;
  const pill = (
    <button
      type="button"
      role="checkbox"
      aria-checked={isChecked}
      data-testid={testId}
      {...(interactive ? {} : { 'aria-disabled': true })}
      {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
      onClick={interactive ? onToggle : undefined}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
        locked
          ? 'border-border bg-muted/40 text-muted-foreground/60 cursor-not-allowed'
          : mandatory
            ? 'border-accent-main text-accent-main cursor-not-allowed bg-white'
            : selected
              ? // Soft tint, not plain white — a selected pill differed from an
                // unselected one only by border/text color before, which read as
                // barely-there at a glance.
                'border-accent-main bg-selected-row text-accent-main cursor-pointer'
              : 'border-border text-muted-foreground hover:border-accent-main/40 cursor-pointer bg-white',
      )}
    >
      {/* Presentational only — the surrounding button owns the role and the click.
          A real <Checkbox> here renders a nested <button>, which is invalid HTML. */}
      <span
        aria-hidden="true"
        className={cn(
          'grid size-3.5 shrink-0 place-content-center rounded-sm border transition-colors',
          isChecked ? 'border-accent-main bg-accent-main text-white' : 'border-muted-foreground/40',
        )}
      >
        {isChecked && <Check className="size-2.5" aria-hidden="true" />}
      </span>
      {label}
      {mandatory && <Lock className="size-3 shrink-0 opacity-70" aria-hidden="true" />}
    </button>
  );

  if (tooltip === undefined) return pill;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
